import { create } from 'zustand'
import type { ReconciliationSession, ReconciliationSessionSummary, User, UserReplaceProfile } from '../types'

export type ViewName =
  | 'upload'
  | 'result'
  | 'history'
  | 'scenarios'
  | 'users'
  | 'logs'
  | 'account'
  | 'replace-profiles'
  | 'bulk-rename'
  | 'pricelists'
  | 'admin-settings'
  | 'audit-log'
  | 'validator'
  | 'testdata-generator'
  | 'party-presets'

export const VIEW_PATHS: Record<ViewName, string> = {
  upload:             '/',
  result:             '/result',
  history:            '/history',
  scenarios:          '/scenarios',
  'bulk-rename':      '/bulk-rename',
  'replace-profiles': '/replace-profiles',
  pricelists:         '/pricelists',
  users:              '/users',
  logs:               '/logs',
  account:            '/account',
  'admin-settings':   '/admin-settings',
  'audit-log':        '/audit-log',
  validator:              '/validator',
  'testdata-generator':   '/testdata-generator',
  'party-presets':        '/party-presets',
}

export function pathToView(pathname: string): ViewName {
  const match = Object.entries(VIEW_PATHS).find(([, path]) => path === pathname)
  return (match?.[0] as ViewName) ?? 'upload'
}

interface AppState {
  currentSession: ReconciliationSession | null
  loadedTestCase: { scenarioName: string; testCaseName: string } | null
  history: ReconciliationSessionSummary[]
  isLoading: boolean
  error: string | null
  selectedLineId: number | null
  activeView: ViewName
  currentUser: User | null
  activeProfile: UserReplaceProfile | null
  language: string

  setCurrentSession: (s: ReconciliationSession | null) => void
  setLoadedTestCase: (t: { scenarioName: string; testCaseName: string } | null) => void
  setHistory: (h: ReconciliationSessionSummary[]) => void
  setLoading: (v: boolean) => void
  setError: (e: string | null) => void
  setSelectedLineId: (id: number | null) => void
  setActiveView: (v: ViewName) => void
  setCurrentUser: (u: User | null) => void
  setActiveProfile: (p: UserReplaceProfile | null) => void
  setLanguage: (lang: string) => void
}

export const useStore = create<AppState>((set) => ({
  currentSession: null,
  loadedTestCase: null,
  history: [],
  isLoading: false,
  error: null,
  selectedLineId: null,
  activeView: pathToView(window.location.pathname),
  currentUser: null,
  activeProfile: null,
  language: 'nl',

  setCurrentSession: (s) => set({ currentSession: s, loadedTestCase: null }),
  setLoadedTestCase: (t) => set({ loadedTestCase: t }),
  setHistory: (h) => set({ history: h }),
  setLoading: (v) => set({ isLoading: v }),
  setError: (e) => set({ error: e }),
  setSelectedLineId: (id) => set({ selectedLineId: id }),
  setActiveView: (v) => {
    window.history.pushState(null, '', VIEW_PATHS[v])
    set({ activeView: v })
  },
  setCurrentUser: (u) => set({ currentUser: u }),
  setActiveProfile: (p) => set({ activeProfile: p }),
  setLanguage: (lang) => set({ language: lang }),
}))
